/**
 * Prix du Canada - Mantine Theme Configuration
 *
 * This theme preserves the exact brand identity:
 * - Primary Gold (#c9a96e) and Deep Gold (#b8956f)
 * - Playfair Display for headings
 * - Lora for body text
 * - Elegant, ballet-inspired aesthetic
 */

// Mantine requires color shades from 0-9
// We'll create a proper gold palette based on your primary gold
const createGoldPalette = () => {
  return [
    '#faf8f5',  // 0 - Lightest (soft-cream)
    '#f5f1ea',  // 1 - Very light
    '#ede6d8',  // 2 - Light
    '#e0d3bf',  // 3 - Light-medium
    '#d4c1a6',  // 4 - Medium-light
    '#c9a96e',  // 5 - PRIMARY GOLD (your main brand color)
    '#b8956f',  // 6 - Deep gold
    '#a08158',  // 7 - Darker
    '#886d48',  // 8 - Very dark
    '#705938',  // 9 - Darkest
  ];
};

const createCharcoalPalette = () => {
  return [
    '#fcfcfc',  // 0 - Warm white
    '#f8f6f3',  // 1 - Light gray
    '#e9e7e4',  // 2 - Very light gray
    '#d1cfc9',  // 3 - Light gray
    '#9e9a91',  // 4 - Medium gray
    '#666666',  // 5 - Soft gray
    '#4a4a4a',  // 6 - Medium charcoal
    '#2c2c2c',  // 7 - PRIMARY CHARCOAL (your main text color)
    '#1f1f1f',  // 8 - Dark charcoal
    '#0a0a0a',  // 9 - Darkest
  ];
};

export const theme = {
  // Color palette - matches your exact brand colors
  colors: {
    gold: createGoldPalette(),
    charcoal: createCharcoalPalette(),
    rose: [
      '#faf6f3',
      '#f4ebe5',
      '#e9d7cc',
      '#ddc3b3',
      '#d4b5a0',  // 4 - Accent rose
      '#c9a691',
      '#b89782',
      '#a78873',
      '#967964',
      '#856a55',
    ],
  },

  // Primary color for buttons, links, etc.
  primaryColor: 'gold',
  primaryShade: 5, // Points to #c9a96e

  // Font family - matches your exact fonts
  fontFamily: "'Lora', 'Georgia', serif",
  fontFamilyMonospace: "'Courier New', monospace",

  // Headings configuration - matches your Playfair Display setup
  headings: {
    fontFamily: "'Playfair Display', 'Georgia', serif",
    fontWeight: 700,
    sizes: {
      h1: {
        fontSize: '3.2rem',   // Matches your h1 exactly
        fontWeight: 800,
        lineHeight: 1.3,
      },
      h2: {
        fontSize: '2.4rem',   // Matches your h2 exactly
        fontWeight: 700,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.8rem',   // Matches your h3 exactly
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h6: {
        fontSize: '1.1rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
    },
  },

  // Font sizes - body text matches your 18px base
  fontSizes: {
    xs: '0.75rem',   // 12px
    sm: '0.875rem',  // 14px
    md: '1rem',      // 16px
    lg: '1.125rem',  // 18px - YOUR BASE SIZE
    xl: '1.5rem',    // 24px
  },

  // Default font size to match your body text
  defaultRadius: 'md',

  // Spacing system
  spacing: {
    xs: '0.5rem',    // 8px
    sm: '0.75rem',   // 12px
    md: '1.25rem',   // 20px
    lg: '2rem',      // 32px
    xl: '3rem',      // 48px
  },

  // Breakpoints - matches your responsive design
  breakpoints: {
    xs: '30em',   // 480px - your mobile breakpoint
    sm: '48em',   // 768px - your tablet breakpoint
    md: '64em',   // 1024px
    lg: '74em',   // 1184px
    xl: '90em',   // 1440px
  },

  // Shadows - matches your subtle shadows
  shadows: {
    xs: '0 1px 3px rgba(201, 169, 110, 0.05)',
    sm: '0 2px 6px rgba(201, 169, 110, 0.08)',       // shadow-light
    md: '0 4px 12px rgba(44, 44, 44, 0.12)',         // shadow-medium
    lg: '0 8px 24px rgba(44, 44, 44, 0.15)',
    xl: '0 16px 48px rgba(44, 44, 44, 0.2)',
  },

  // Component-specific overrides to match your design
  components: {
    Button: {
      styles: (theme) => ({
        root: {
          fontWeight: 600,
          letterSpacing: '0.02em',
          transition: 'all 0.3s ease',
          // Will inherit primary gold color
        },
      }),
      defaultProps: {
        size: 'md',
      },
    },

    Title: {
      styles: (theme) => ({
        root: {
          letterSpacing: '-0.01em', // Matches your headings
        },
      }),
    },

    Text: {
      styles: (theme) => ({
        root: {
          lineHeight: 1.8,  // Matches your body line-height
          fontSize: '1.1rem', // Matches your paragraph size
          fontWeight: 500,    // Matches your body weight
        },
      }),
    },

    Container: {
      defaultProps: {
        sizes: {
          xs: 540,
          sm: 720,
          md: 960,
          lg: 1140,
          xl: 1320,
        },
      },
    },

    Modal: {
      styles: (theme) => ({
        header: {
          fontFamily: "'Playfair Display', 'Georgia', serif",
        },
      }),
    },

    Card: {
      styles: (theme) => ({
        root: {
          backgroundColor: theme.colors.gold[0], // Soft cream
          borderRadius: theme.radius.md,
        },
      }),
    },

    Input: {
      styles: (theme) => ({
        input: {
          fontSize: '1rem',
          '&:focus': {
            borderColor: theme.colors.gold[5], // Primary gold
          },
        },
      }),
    },
  },

  // Global styles to match your current design
  globalStyles: (theme) => ({
    '*, *::before, *::after': {
      boxSizing: 'border-box',
    },

    html: {
      background: `linear-gradient(135deg, ${theme.colors.gold[5]} 0%, ${theme.colors.gold[6]} 100%)`,
    },

    body: {
      fontFamily: theme.fontFamily,
      fontSize: '18px',           // Your base size
      lineHeight: 1.8,            // Your line height
      color: theme.colors.charcoal[7],  // Your charcoal
      background: `linear-gradient(135deg, ${theme.colors.gold[5]} 0%, ${theme.colors.gold[6]} 100%)`,
      fontWeight: 500,
      textAlign: 'left',
    },

    h1: {
      color: theme.colors.charcoal[7],
    },

    h2: {
      color: theme.colors.gold[5], // Primary gold for h2
      marginBottom: '1.2em',
    },

    p: {
      marginBottom: '1.4em',
      color: theme.colors.charcoal[7],
    },
  }),

  // Other properties
  black: '#2c2c2c',  // Your charcoal
  white: '#fcfcfc',  // Your warm white

  defaultGradient: {
    from: 'gold.5',
    to: 'gold.6',
    deg: 135,
  },

  // Ensure smooth transitions
  transitionTimingFunction: 'ease',
};

export default theme;
